import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Plus, Search, Trash2, AlertTriangle, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchIngredients,
  getWatchlist,
  addWatchlistItem,
  removeWatchlistItem,
  getWatchlistNotifications,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";

const RISK_TONE = {
  HIGH: "bg-rose-50 text-rose-700 border-rose-200",
  MODERATE: "bg-amber-50 text-amber-800 border-amber-200",
  LOW: "bg-lime-50 text-lime-700 border-lime-200",
};

export default function Watchlist({ onAuthNeeded }) {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [allIngredients, setAllIngredients] = useState([]);
  const [items, setItems] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) return undefined;
    if (!user) {
      onAuthNeeded?.();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [ings, wl, nt] = await Promise.all([
          fetchIngredients(),
          getWatchlist(),
          getWatchlistNotifications(false),
        ]);
        if (!cancelled) {
          setAllIngredients(ings);
          setItems(wl.items || []);
          setNotifs(nt.notifications || []);
        }
      } catch (_e) {
        if (!cancelled) toast.error("Couldn't load your watchlist", {
          description: "Check your connection and try again \u2014 nothing was lost.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ready]);

  const watchedIds = useMemo(
    () => new Set(items.map((i) => i.ingredient_id)),
    [items],
  );
  const filteredIngs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? allIngredients.filter(
          (i) =>
            (i.name || "").toLowerCase().includes(q) ||
            (i.inci_name || "").toLowerCase().includes(q) ||
            (i.aliases || []).some((a) => (a || "").toLowerCase().includes(q)),
        )
      : allIngredients;
    return list.slice(0, 40);
  }, [filter, allIngredients]);

  const add = async (ing) => {
    try {
      await addWatchlistItem(ing.id);
      setItems((prev) => [
        ...prev,
        { ingredient_id: ing.id, name: ing.name, category: ing.category, added_at: new Date().toISOString() },
      ]);
      toast.success(`Watching ${ing.name}`, {
        description: "We'll surface this ingredient whenever it shows up in your future checks.",
      });
    } catch (_e) {
      toast.error("Couldn't add to watchlist", {
        description: "Check your connection and try again \u2014 nothing was changed.",
      });
    }
  };
  const remove = async (ing_id) => {
    try {
      await removeWatchlistItem(ing_id);
      setItems((prev) => prev.filter((i) => i.ingredient_id !== ing_id));
      toast.message("Removed from watchlist", {
        description: "Re-add anytime from any ingredient page.",
      });
    } catch (_e) {
      toast.error("Couldn't remove that item", {
        description: "Check your connection and try again \u2014 your watchlist is unchanged.",
      });
    }
  };

  if (!ready || !user) return null;

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 bg-cream-50" data-testid="watchlist-page">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs uppercase tracking-[0.22em] font-bold text-peach-700 mb-3 flex items-center gap-2">
          <Bell className="w-3.5 h-3.5" /> Watchlist
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-ink">
          Your ingredient watchlist
        </h1>
        <p className="mt-2 text-ink-mute max-w-xl">
          Watch ingredients you're sensitive to. We'll alert you whenever a new
          medication interaction involving them is added or verified.
        </p>

        {/* Notifications strip */}
        {notifs.length > 0 && (
          <div
            data-testid="watchlist-notifications"
            className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5"
          >
            <div className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="w-4 h-4" />
              <p className="text-xs uppercase tracking-[0.2em] font-bold">
                {notifs.length} new interaction{notifs.length > 1 ? "s" : ""} involving watched ingredients
              </p>
            </div>
            <ul className="mt-3 space-y-2">
              {notifs.slice(0, 6).map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    "rounded-2xl border p-3 text-sm flex items-start gap-3 bg-white",
                    RISK_TONE[n.risk_level] || "border-surface-line",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white border">
                    {n.risk_level}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-ink">
                      {n.ingredient_name} × {n.medication_name}
                    </p>
                    <p className="text-ink-soft text-[13px]">{n.summary}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Watching list */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-ink mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-peach-600" />
            Watching ({items.length})
          </h2>
          {items.length === 0 ? (
            <p className="text-ink-mute text-sm">No ingredients yet — add some below.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {items.map((i) => (
                <span
                  key={i.ingredient_id}
                  data-testid={`watchlist-item-${i.ingredient_id}`}
                  className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-surface-line text-sm font-medium"
                >
                  {i.name || i.ingredient_id}
                  <button
                    type="button"
                    onClick={() => remove(i.ingredient_id)}
                    className="inline-flex w-6 h-6 items-center justify-center rounded-full text-ink-mute hover:bg-rose-50 hover:text-rose-600"
                    aria-label={`Stop watching ${i.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Add */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-ink mb-3">Add an ingredient</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-light" />
            <input
              data-testid="watchlist-add-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search INCI names, common names, aliases…"
              className="w-full h-12 pl-10 pr-3 rounded-2xl bg-white border border-surface-line text-sm focus:outline-none focus:ring-4 focus:ring-peach-100"
            />
          </div>
          {loading ? (
            <p className="text-ink-mute text-sm mt-3">Loading catalog…</p>
          ) : (
            <ul className="mt-3 divide-y divide-surface-line rounded-2xl bg-white border border-surface-line overflow-hidden">
              {filteredIngs.map((i) => {
                const already = watchedIds.has(i.id);
                return (
                  <li key={i.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{i.name}</p>
                      <p className="text-xs text-ink-mute truncate">{i.category}</p>
                    </div>
                    <Button
                      variant={already ? "outline" : "default"}
                      disabled={already}
                      data-testid={`watchlist-add-${i.id}`}
                      onClick={() => add(i)}
                      className="h-8 px-3 text-xs"
                    >
                      {already ? "Watching" : (<><Plus className="w-3 h-3 mr-1" /> Watch</>)}
                    </Button>
                  </li>
                );
              })}
              {filteredIngs.length === 0 && (
                <li className="px-4 py-6 text-sm text-ink-mute text-center">
                  No matching ingredients.
                </li>
              )}
            </ul>
          )}
        </div>

        <Button
          variant="outline"
          onClick={() => navigate("/check")}
          className="mt-8"
          data-testid="watchlist-to-checker"
        >
          Back to checker
        </Button>
      </div>
    </main>
  );
}
